// assemble — Track B step 2. Build the whole §I–§VII document from ONE live passport as a
// stack of self-managed section artboards, with cross-section renumbering applied.
//
// There is NO document layer over the stack (ratified with Kirill / CD, s415): no multipage
// container, cover, running header, or continuous page number. The document = the sections in
// order, each already self-paginating; the assembler only decides PRESENCE and NUMBERING:
//
//   · §I–§IV, §VII always present.
//   · §V removed on the zero_spread_all_layers provisor (viewV → absent).
//   · §VI removed when real quotes < 2 (silent skip). F5/s882.d1: a judge transport failure is
//     NOT a special case — it yields zero real quotes and takes the same removal path.
//   · romans are assigned by document position; the cascade (both §V and §VI gone) shifts once.
//
// The frozen sections bake their own roman (only §VII has a hook: skipVi → VI). The §V-absent
// shifts (§VI→V, §VII→V) have no section hook, so — per CD, "renumbering is assembly logic" —
// the assembler remaps the two numbering fields of the rendered section HTML. shiftRoman only
// touches the runner-roman span and the foot-page roman token; everything else is untouched.

import { renderToStaticMarkup } from 'react-dom/server';
import { createElement as h } from 'react';
import Artboard from './Artboard.jsx';
import SectionI from './sections/SectionI.jsx';
import SectionII from './sections/SectionII.jsx';
import SectionIII from './sections/SectionIII.jsx';
import SectionIV from './sections/SectionIV.jsx';
import SectionV from './sections/SectionV.jsx';
import SectionVI from './sections/SectionVI.jsx';
import SectionVII from './sections/SectionVII.jsx';
import { viewI, viewII, viewIII, viewIV, viewV, viewVI, viewVII } from './viewFromPassport.js';
import { validateWizardInput } from '../contracts.js';

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII'];
const MIN_QUOTES = 2; // §VI section threshold (s280) — must match SectionVI

/** Count the real (non-reason) quotes a §VI view carries. */
function realQuoteCount(vVI) {
  const stack = (vVI.stack || []).filter((c) => !c.reason).length;
  const qn = vVI.qn && !vVI.qn.reason ? 1 : 0;
  return stack + qn;
}

/**
 * Decide which sections print and what roman each gets. Pure — the export/render layers build
 * on this. Returns { present: [{id, roman}], vAbsent, viAbsent, cascade }.
 */
export function documentPlan(passport) {
  const vV = viewV(passport);
  const vVI = viewVI(passport);
  const vAbsent = !!vV.absent;
  // F5/s882.d1: a judge transport failure is no longer a §VI-present exception. It yields zero
  // real quotes (every card carries a reason), so it falls through the same threshold as N < 2.
  const viAbsent = realQuoteCount(vVI) < MIN_QUOTES;
  const cascade = vAbsent && viAbsent;

  const ids = ['I', 'II', 'III', 'IV'];
  if (!vAbsent) ids.push('V');
  if (!viAbsent) ids.push('VI');
  ids.push('VII');

  const present = ids.map((id, i) => ({ id, roman: ROMAN[i] }));
  return { present, vAbsent, viAbsent, cascade };
}

// Remap the two numbering fields of a rendered section from one roman to another.
const escRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
export function shiftRoman(html, from, to) {
  if (from === to) return html;
  // Global — a section can emit MULTIPLE sheets (a paginated §VI), each with its own runner-roman
  // and foot-page; a first-match replace would leave sheets 2+ mis-numbered.
  return html
    .replaceAll(`<span class="runner-roman">${from}</span>`, `<span class="runner-roman">${to}</span>`)
    .replace(new RegExp(`(<span class="foot-page"[^>]*>)${escRe(from.toLowerCase())}( · )`, 'g'), `$1${to.toLowerCase()}$2`);
}

/**
 * Render one present section to its artboard HTML string, numbering applied.
 * @param entry  {id, roman} from documentPlan().present
 */
export function renderSectionHtml(entry, passport, opts = {}) {
  const { id, roman } = entry;
  const run = opts.run || (passport.input && passport.input.meta && passport.input.meta.run) || 'live';
  switch (id) {
    case 'I':
      return renderToStaticMarkup(
        h(Artboard, { sectionMark: 'i · cover', screenLabel: '§I', run }, h(SectionI, { view: viewI(passport, { generatedAt: opts.generatedAt }) })),
      );
    case 'II':
      // §II plan: measured by `settleSectionII` (docFit.js) on the static roads and passed back
      // as opts.plans.II; without it the section's declared single sheet renders (s969).
      return renderToStaticMarkup(h(SectionII, {
        view: { ...viewII(passport), screen: '§II', source: run, ...(opts.plans && opts.plans.II ? { plan: opts.plans.II } : {}) },
      }));
    case 'III':
      return renderToStaticMarkup(h(SectionIII, { view: { ...viewIII(passport, { id: 'iii' }), screen: '§III', source: run, ...(opts.generatedAt ? { generatedAt: opts.generatedAt } : {}) } }));
    case 'IV':
      return renderToStaticMarkup(h(SectionIV, { view: { ...viewIV(passport, { id: 'iv' }), screen: '§IV', source: run } }));
    case 'V':
      // §V is always at position V when present (I–IV are fixed), so no shift is ever needed.
      return renderToStaticMarkup(h(SectionV, { view: { ...viewV(passport), id: 'v', screen: '§V', source: run } }));
    case 'VI': {
      // §VI needs sheets (pagination) — SSR default is a single sheet; the browser re-measures.
      const vVI = viewVI(passport);
      const realStack = (vVI.stack || []).filter((c) => !c.reason).length;
      const view = { ...vVI, screen: '§VI', source: run, sheets: vVI.sheets || [{ take: realStack, cls: '', note: '' }] };
      let html = renderToStaticMarkup(h(SectionVI, { view }));
      if (roman !== 'VI') html = shiftRoman(html, 'VI', roman); // §V absent → §VI → V
      return html;
    }
    case 'VII': {
      // native skipVi → VI; the cascade (→V) is a further remap the section can't express.
      const skipVi = roman !== 'VII';
      const view = { ...viewVII(passport, { skipVi }), screen: '§VII', source: run };
      let html = renderToStaticMarkup(h(SectionVII, { view }));
      if (roman === 'V') html = shiftRoman(html, 'VI', 'V'); // cascade: skipVi gave VI, shift to V
      return html;
    }
    default:
      return '';
  }
}

/** The whole document as an array of section-artboard HTML strings, in order. */
/**
 * Precondition: the document needs stage METADATA, not just slider rows (D&R s511.d4).
 *
 * `WizardInput.stages` is optional in the contract — the engine computes geometry from sliders
 * alone, which is why the engine-only datasets (02/03/05/07) omit it. The DOCUMENT cannot: §II
 * prints stage cards, §III and §IV draw a stage axis. Without it §III threw
 * "Cannot read properties of undefined (reading 'openStart')" and §IV followed — a TypeError
 * from the middle of a render, blaming the wrong section.
 *
 * This is NOT a degradation state: the wizard cannot produce it (step 2 collects a name and a
 * start for every stage, FLOOR=3) and neither can an import (`parse-extract` always emits stage
 * metadata). It is a fixture shape, so it fails loudly at the seam instead of being drawn as
 * something a reader could mistake for their life.
 */
function assertRenderable(passport) {
  const stages = passport && passport.input && passport.input.wizard && passport.input.wizard.stages;
  const rows = ((passport && passport.input && passport.input.wizard && passport.input.wizard.sliders) || []).length;
  if (!Array.isArray(stages) || stages.length === 0) {
    throw new Error(
      `the document needs stage metadata: input.wizard.stages is ${stages === undefined ? 'absent' : 'empty'} while the passport carries ${rows} slider row(s). `
      + 'Sliders alone drive the engine but not the page — §II prints stage cards, §III/§IV draw a stage axis. '
      + 'Engine-only fixtures (datasets 02/03/05/07) are not assemblable by design (D&R s511.d4).',
    );
  }
  if (stages.length !== rows) {
    throw new Error(`the document needs one stage per slider row: ${stages.length} stage(s) against ${rows} slider row(s) (D&R s511.d4)`);
  }

  // cc-31 — THE CONTRACT BECOMES A GUARD HERE, and this is the only place it does.
  //
  // Until now nothing on the way to a rendered document called `validateWizardInput`. The check
  // existed, knew how to catch a slider outside [-3,3], a 16th stage, an unresolved `__cust`
  // sentinel and an inverted interval — and every one of those still reached the printed page,
  // because the seam only asked the two questions above. Measured: a passport built from
  // out-of-order step-2 rows assembled cleanly and §II printed "03/2011 — 06/2004", a stretch of
  // life ending seven years before it begins.
  //
  // Throwing is right HERE and wrong on the live wizard path. This seam is reached by imports,
  // fixtures and our own runs, where nobody is harmed and a loud refusal beats a wrong page. A
  // person mid-wizard must never meet it: losing twelve minutes of work at submit is worse than
  // one odd date. That is why `wizardSubmitCheck` (src/wizard/submitCheck.js) exists and runs
  // BEFORE the passport is built — this throw is the backstop for when it was not called.
  //
  // Cost of switching it on, measured before doing it: 29 live extracts + 8 frozen datasets =
  // ZERO validation errors. Nothing real was relying on the silence.
  const problems = validateWizardInput(passport.input);
  if (problems.length) {
    // ⛔ ТЕКСТЫ ПРОБЛЕМ — В `error.problems`, НЕ В СООБЩЕНИИ (`s946.d3`). Валидатор называет в них
    // значения ползунков и годы этапов, а сообщение уходит в Sentry. Сторонние поля ошибки SDK
    // по умолчанию не сериализует; тесту и отладке они доступны как были.
    throw Object.assign(new Error(
      `the passport does not satisfy the wizard-input contract, so the document is not built: ${problems.length} problem(s), see error.problems. `
      + 'On the wizard path this must be caught at submit by wizardSubmitCheck() — reaching the assembler means that call was skipped (cc-31).',
    ), { problems });
  }
}

export function assembleSections(passport, opts = {}) {
  assertRenderable(passport);
  const plan = documentPlan(passport);
  return plan.present.map((entry) => renderSectionHtml(entry, passport, opts));
}

/** React component: the stacked document (for the dev harness / browser render). */
export function AssembledDocument({ passport, run }) {
  const htmls = assembleSections(passport, { run });
  return h(
    'div',
    { className: 'lm-document' },
    htmls.map((html, i) => h('div', { key: i, dangerouslySetInnerHTML: { __html: html } })),
  );
}
